import { minorUnitExponent } from '@/design/format'

/*
 * The demo lists' CSV files, written the way the API writes its exports: money in major
 * units, instants with `+00:00`, website text formula-safe, a null as an empty cell.
 */

/** Major units to the currency's own decimals; empty for a null. */
export function csvMoney(minor: number | null, currency: string): string {
  if (minor === null) return ''
  const digits = minorUnitExponent(currency)
  return (minor / 10 ** digits).toFixed(digits)
}

/** Website text, formula-safe (a leading `=`, `+`, `-`, `@`, tab or CR gets a `'`) and quoted when it must be. */
export function csvText(value: string | null): string {
  if (value === null) return ''
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

export function csvPlain(value: number | boolean | null): string {
  return value === null ? '' : String(value)
}

/** An instant with `+00:00`, as Python's `isoformat()` writes it. */
export function csvInstant(value: string | null): string {
  return value === null ? '' : value.replace(/Z$/, '+00:00')
}

/** A ratio as a Python float: `1.0`, never `1`. */
export function csvFraction(value: number | null): string {
  return value === null ? '' : Number.isInteger(value) ? value.toFixed(1) : String(value)
}

/** A CSV column: its header, and the cell it writes for a row. */
export type MockCsvColumn<Row> = readonly [header: string, cell: (row: Row) => string]

/**
 * The file: one list of columns builds the header line and every row, so a cell can
 * never sit under another column's header.
 */
export function csvBlob<Row>(columns: readonly MockCsvColumn<Row>[], rows: readonly Row[]): Blob {
  const header = columns.map(([name]) => name).join(',')
  const lines = rows.map((row) => columns.map(([, cell]) => cell(row)).join(','))
  return new Blob([[header, ...lines].join('\n') + '\n'], { type: 'text/csv' })
}
